import { DownloadOutlined } from '@ant-design/icons';
import { Col, Form, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import _timezone from 'dayjs/plugin/timezone';
import { concat, endsWith } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import fileDownload from 'services/fileDownload';
import emiseApi from 'services/korporatniAkce/emise';
import { ISezemiseKorpAkce } from 'services/korporatniAkce/kontrolaXMLSRD';
import {
    IPruvodceVH,
    IPruvodceVHBodyvh,
    IPruvodceVHUcastvh,
    pruvodceVHGETApi,
    pruvodceVHPOSTApi,
    pruvodceVHPUTApi,
    pruvodceVHcertApi
} from 'services/korporatniAkce/pruvodceVH';
import { zhrnutiVHdownload } from 'services/korporatniAkce/zhrnutiDownload';

import { EKvorumEnums } from 'enums/akcionarKorpAkce/kvorumEnums';
import { EZpusob } from 'enums/akcionarKorpAkce/zpusob';
import { EAnoNe } from 'enums/anoNe';
import { ENotificationType } from 'enums/notificationType';
import routePathEnum from 'enums/routePathEnum';

import identifikaceOsobySelect from 'data/codebooks/anoNeSelect';
import { AdmissionConditionsSelect } from 'data/codebooks/korporatniAkce/AdmissionConditionsSelect';
import { hlasovaniData } from 'data/codebooks/korporatniAkce/hlasovaniData';
import { kvorumSelect } from 'data/codebooks/korporatniAkce/kvorumSelect';
import { prahSelect } from 'data/codebooks/korporatniAkce/prahSelect';
import { proxyTypeSelect } from 'data/codebooks/korporatniAkce/proxyTypeSelect';
import { typVHSelectData } from 'data/codebooks/korporatniAkce/typVHSelectData';
import { votetpData } from 'data/codebooks/korporatniAkce/votetpData';
import { zpusobSelectData } from 'data/codebooks/korporatniAkce/zpusobSelectData';

import bicValidation from 'utils/validations/bicValidation';
import emailValidation from 'utils/validations/emailValidation';
import leiValidation from 'utils/validations/leiValidation';
import numberValidation from 'utils/validations/numberValidation';
import { procentaValidation } from 'utils/validations/procentaValidation';
import urlValidation from 'utils/validations/urlValidation';

let utc = require('dayjs/plugin/utc');
let timezone = require('dayjs/plugin/timezone');

const tz = 'Europe/Prague';

console.log(_timezone);
dayjs.extend(utc);
dayjs.extend(timezone);

interface PruvodceVHProps {}

export const PruvodceVH: React.FC<PruvodceVHProps> = () => {
    const [form] = Form.useForm();
    const [formModal] = Form.useForm();
    const { navigateWorkaround } = useNavigateWorkaround();
    const { idSRD, isinSRD, aktivniFirma, prevPath, cisStaty, NotificationComponent, profily } = useContext(Context);
    const { setLevel } = useProfily();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingPXklic, setLoadingPXklic] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [, setEmise] = useState<ISezemiseKorpAkce[]>([]);
    const [openUcast, setOpenUcast] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<'pdf' | 'xml' | ''>('');
    const [dataUcast, setDataUcast] = useState<IPruvodceVHUcastvh>();
    const [openBod, setOpenBod] = useState<boolean>(false);
    const [putData, setPutData] = useState<boolean>(false);
    const [popisMaxLengthErr, setPopisMaxLengthErr] = useState<{
        nazev: boolean;
        popis: boolean;
        nazevEN: boolean;
        popisEN: boolean;
    }>({
        nazev: false,
        popis: false,
        nazevEN: false,
        popisEN: false
    });
    const [dataBod, setDataBod] = useState<IPruvodceVHBodyvh>();
    const [bodEditIndex, setBodEditIndex] = useState<number>(-1);
    const [data, setData] = useState<IPruvodceVH>({
        isin: isinSRD || undefined,
        ucastvh: [],
        bodyvh: []
    });

    useEffect(() => {
        setLoading(true);
        emiseApi()
            .then(resp => {
                setEmise(resp.data.sezemise);
                setData(val => ({
                    ...val,
                    leiemitent: resp.data.sezemise.find(({ isin }) => isin === isinSRD)?.lei
                }));
                if (
                    idSRD &&
                    (endsWith(prevPath, routePathEnum.KORPORATNI_AKCE) ||
                        endsWith(prevPath, routePathEnum.DETAIL_KORPORATNI_AKCE) ||
                        data.typpruvodce === 'REPL')
                ) {
                    return pruvodceVHGETApi(idSRD).then(resp2 => {
                        setData(resp2.data);
                        form.setFieldsValue({
                            SRD_PRUVODCE_VH_B_ID: resp2.data.mtgid,
                            LEI_CELE: resp2.data.leiemitent
                        });
                    });
                }
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idSRD, isinSRD, prevPath]);

    // nastavenie casovej zony pre tuto page
    useEffect(() => {
        dayjs.tz.setDefault('Europe/Prague');
        return () => {
            dayjs.tz.setDefault();
        };
    }, []);

    useEffect(() => {
        formModal.setFieldsValue({
            typprahu: dataBod?.typprahu || dataBod?.kvorum?.typprahu
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataBod]);

    useEffect(() => {
        if (putData === true) {
            idSRD &&
                data &&
                pruvodceVHPUTApi(
                    idSRD,
                    data,
                    endsWith(prevPath, routePathEnum.DETAIL_KORPORATNI_AKCE) || data.typpruvodce === 'REPL'
                        ? 'REPL'
                        : 'NEWM'
                );
            setPutData(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [putData]);

    /** Clean-up effect */
    useEffect(() => {
        return () => {
            setLevel('idSRD', null);
            setLevel('prevPath', '');
        };
    }, [setLevel]);

    const nextPage = () => {
        if (!idSRD || !data) setPage(pv => pv + 1);
        else
            pruvodceVHPUTApi(
                idSRD,
                data,
                endsWith(prevPath, routePathEnum.DETAIL_KORPORATNI_AKCE) || data.typpruvodce === 'REPL'
                    ? 'REPL'
                    : 'NEWM'
            ).then(() => setPage(pv => pv + 1));
    };

    const sendVH = () => {
        if (!idSRD || !data) return;
        profily?.typprihlaseni !== 'PXKLIC' ? setLoading(true) : setLoadingPXklic(true);
        (profily?.typprihlaseni === 'CERT'
            ? pruvodceVHcertApi(
                  idSRD,
                  data,
                  endsWith(prevPath, routePathEnum.DETAIL_KORPORATNI_AKCE) || data.typpruvodce === 'REPL'
                      ? 'REPL'
                      : 'NEWM'
              )
            : pruvodceVHPOSTApi(
                  idSRD,
                  data,
                  endsWith(prevPath, routePathEnum.DETAIL_KORPORATNI_AKCE) || data.typpruvodce === 'REPL'
                      ? 'REPL'
                      : 'NEWM'
              )
        )
            .then(resp => {
                if ((resp.data.upozor || []).length) {
                    resp.data.upozor.map(e =>
                        NotificationComponent({
                            title: e.typ
                        })
                    );
                } else {
                    NotificationComponent({
                        title: 'TYPE_VYPIS',
                        description: 'ZADOST_ODESLANA'
                    });
                    navigateWorkaround(`${routePathEnum.FIRMA_PLAIN}${aktivniFirma}${routePathEnum.KORPORATNI_AKCE}`);
                }
            })
            .catch((err: any) =>
                NotificationComponent({
                    title: err.response?.data.code,
                    description: err.response?.data.message,
                    type: ENotificationType.ERROR
                })
            )
            .finally(() => (profily?.typprihlaseni !== 'PXKLIC' ? setLoading(false) : setLoadingPXklic(false)));
    };

    const downloadZhrnuti = (type: 'pdf' | 'xml') => {
        idSRD && setDownloading(type);
        idSRD &&
            zhrnutiVHdownload(
                idSRD,
                data,
                type,
                endsWith(prevPath, routePathEnum.DETAIL_KORPORATNI_AKCE) || data.typpruvodce === 'REPL'
                    ? 'REPL'
                    : 'NEWM'
            )
                .then(resp => fileDownload(resp.data.redirect))
                .finally(() => setDownloading(''));
    };

    return (
        <LoadingWrapper
            loading={loading || loadingPXklic}
            loadingText={profily?.typprihlaseni === 'PXKLIC' && loadingPXklic ? 'LOADING_PXKLIC' : 'NACITANI'}
        >
            <FormWrapper
                onFinish={() => {
                    sendVH();
                }}
                outForm={form}
            >
                <Title level={3} text="TYPE_VH" />
                {[1, 5].includes(page) && (
                    <>
                        <Title level={5} dividerTitle={true} text="SRD_PRUVODCE_VH_A" />
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="ISIN"
                                    label="ISIN"
                                    capitalize={true}
                                    initialValue={isinSRD || undefined}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            isin: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 5}
                                />
                            </Col>
                        </Row>
                        <Title level={5} dividerTitle={true} text="UDAJE_O_EMITENTOVI" />
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="LEI_CELE"
                                    label="LEI_CELE"
                                    initialValue={data.leiemitent}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            leiemitent: value.target.value
                                        }))
                                    }
                                    rules={leiValidation()}
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 5}
                                />
                            </Col>
                        </Row>
                        <Title level={5} dividerTitle={true} text="SRD_PRUVODCE_VH_B" />
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="SRD_PRUVODCE_VH_B_ID"
                                    label="SRD_PRUVODCE_VH_B_ID"
                                    initialValue={
                                        (form.getFieldValue('SRD_PRUVODCE_VH_B_ID') || `${isinSRD}-`) +
                                        dayjs().format('YYYY') +
                                        '-'
                                    }
                                    maxLength={35}
                                    //  value={data.mtgid}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            mtgid: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 5}
                                    rules={[
                                        () => ({
                                            validator(_rule, value: string) {
                                                if (!value || (value.includes('-') && value.split('-')[2].length > 0))
                                                    return Promise.resolve();
                                                return Promise.reject(
                                                    <TranslateText>NEVALIDNI_IDENTIFIKATOR</TranslateText>
                                                );
                                            }
                                        })
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                )}
                {[2, 5].includes(page) && (
                    <>
                        <Title level={5} dividerTitle={true} text="SRD_PRUVODCE_VH_C" />
                        <Row>
                            <Col span={24}>
                                <DatePickerComponent
                                    name="SRD_PRUVODCE_VH_C_DATUM"
                                    label="SRD_PRUVODCE_VH_C_DATUM"
                                    showTime={true}
                                    initialValue={data.datumkonani ? dayjs(data.datumkonani).tz(tz) : undefined}
                                    disabledDate={current =>
                                        current.tz(tz).format('YYYY-MM-DD') < dayjs().tz(tz).format('YYYY-MM-DD')
                                    }
                                    format={value => `${value.utc().tz(tz).format('DD.MM.YYYY HH:mm')} (CET/CEST)`}
                                    labelCol={{
                                        span: 8
                                    }}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            datumkonani: value.utc().tz(tz).format()
                                        }))
                                    }
                                    disabled={page === 5}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <SelectComponent
                                    name="SRD_PRUVODCE_VH_C_TYPVH"
                                    labelParagraph="SRD_PRUVODCE_VH_C_TYPVH"
                                    defaultValue={data.typvh}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            typvh: value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    data={typVHSelectData}
                                    isFormItem={true}
                                    disabled={page === 5}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="SRD_PRUVODCE_VH_C_URLSCHUZKY"
                                    label="SRD_PRUVODCE_VH_C_URLSCHUZKY"
                                    initialValue={data.urlschuzky}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            urlschuzky: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 5}
                                    rules={urlValidation()}
                                    maxLength={2048}
                                />
                            </Col>
                        </Row>

                        <Col span={24}>
                            <DatePickerComponent
                                name="SRD_DLAZDICE_ROZHODNY_DEN"
                                label="SRD_DLAZDICE_ROZHODNY_DEN"
                                // disabledDate={current => current < dayjs.tz()}
                                initialValue={data.rozhodnyden ? dayjs(data.rozhodnyden).tz(tz) : undefined}
                                labelCol={{
                                    span: 8
                                }}
                                format="DD.MM.YYYY"
                                onChange={value =>
                                    setData(val => ({
                                        ...val,
                                        rozhodnyden: value?.utc().tz(tz).format('YYYY-MM-DD')
                                    }))
                                }
                                disabled={page === 5}
                            />
                        </Col>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="ULICE1"
                                    label="ULICE_CP"
                                    initialValue={data.mistokonanivh?.ulice}
                                    maxLength={70}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            mistokonanivh: {
                                                ...val.mistokonanivh,
                                                ulice: value.target.value
                                            }
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 5}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="MESTO1"
                                    label="MESTO"
                                    maxLength={35}
                                    initialValue={data.mistokonanivh?.mesto}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            mistokonanivh: {
                                                ...val.mistokonanivh,
                                                mesto: value.target.value
                                            }
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 5}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="PSC1"
                                    label="PSC"
                                    maxLength={16}
                                    initialValue={data.mistokonanivh?.psc}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            mistokonanivh: {
                                                ...val.mistokonanivh,
                                                psc: value.target.value
                                            }
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 5}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <SelectComponent
                                    name="STAT1"
                                    labelParagraph="STAT"
                                    defaultValue={data.mistokonanivh?.stat}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            mistokonanivh: {
                                                ...val.mistokonanivh,
                                                stat: value
                                            }
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    data={cisStaty}
                                    isFormItem={true}
                                    disabled={page === 5}
                                />
                            </Col>
                        </Row>
                    </>
                )}
                {[3, 5].includes(page) && (
                    <>
                        <Title level={5} dividerTitle={true} text="SRD_PRUVODCE_VH_D" />
                        <TableComponent
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                            style={{
                                margin: '30px 0px'
                            }}
                            rowKey={(_, index) => index || 0}
                            dataSource={openUcast ? [...(data?.ucastvh || [])] : data?.ucastvh}
                            pagination={false}
                            columns={[
                                {
                                    dataIndex: 'jmeno',
                                    key: 'jmeno',
                                    render: (_text: IPruvodceVHUcastvh, row: IPruvodceVHUcastvh) => (
                                        <p
                                            style={{
                                                margin: 0
                                            }}
                                        >
                                            <TranslateText>TYP</TranslateText>:{' '}
                                            <TranslateText>
                                                {`SRD_PRUVODCE_VH_D_PRIDAT_ZPUSOB_${row.zpusob}`}
                                            </TranslateText>{' '}
                                            <TranslateText>DATUM</TranslateText>:{' '}
                                            {dayjs(row.datumoznameni).tz(tz).format('DD.MM.YYYY HH:mm')}
                                        </p>
                                    )
                                },
                                page !== 5
                                    ? {
                                          width: 100,
                                          render: (
                                              _text: IPruvodceVHUcastvh,
                                              _row: IPruvodceVHUcastvh,
                                              index: number
                                          ) => (
                                              <>
                                                  <AnchorComponent
                                                      id={`upravitDbtn${index}`}
                                                      key={index}
                                                      onClick={() => {
                                                          setOpenUcast(true);
                                                          setDataUcast(data.ucastvh[index]);
                                                          setBodEditIndex(index);
                                                          formModal.setFieldsValue({
                                                              ...data.ucastvh[index].mail,
                                                              proxytype: data.ucastvh[index].proxytype,
                                                              typfyzucasti: data.ucastvh[index].typfyzucasti,
                                                              zpusob: data.ucastvh[index].zpusob,
                                                              druh:
                                                                  (data.ucastvh[index] &&
                                                                      data.ucastvh[index].evot?.length &&
                                                                      data.ucastvh[index].evot![0].druh) ||
                                                                  undefined,
                                                              channel:
                                                                  (data.ucastvh[index] &&
                                                                      data.ucastvh[index].evot?.length &&
                                                                      data.ucastvh[index].evot![0].channel) ||
                                                                  undefined,
                                                              bic:
                                                                  (data.ucastvh[index] &&
                                                                      data.ucastvh[index].evot?.length &&
                                                                      data.ucastvh[index].evot![0].bic) ||
                                                                  undefined,
                                                              datum: dayjs(data.ucastvh[index].datumoznameni).tz(tz)
                                                          });
                                                      }}
                                                  >
                                                      <TranslateText>BUTTON_UPRAVIT</TranslateText>
                                                  </AnchorComponent>
                                                  <AnchorComponent
                                                      id={`odebratDbtn${index}`}
                                                      style={{
                                                          marginLeft: 16
                                                      }}
                                                      key={index + 'b'}
                                                      onClick={() => {
                                                          setData(val => {
                                                              const newState = [...val.ucastvh];
                                                              newState.splice(index, 1);
                                                              return {
                                                                  ...val,
                                                                  ucastvh: newState
                                                              };
                                                          });
                                                          setPutData(true);
                                                      }}
                                                  >
                                                      <TranslateText>ODEBRAT</TranslateText>
                                                  </AnchorComponent>
                                              </>
                                          )
                                      }
                                    : {
                                          width: 100,
                                          render: (
                                              _text: IPruvodceVHUcastvh,
                                              _row: IPruvodceVHUcastvh,
                                              index: number
                                          ) => (
                                              <AnchorComponent
                                                  id={`zobrazitDbtn${index}`}
                                                  key={index}
                                                  onClick={() => {
                                                      setOpenUcast(true);
                                                      setDataUcast(data.ucastvh[index]);
                                                      setBodEditIndex(index);
                                                      formModal.setFieldsValue({
                                                          ...data.ucastvh[index].mail,
                                                          proxytype: data.ucastvh[index].proxytype,
                                                          typfyzucasti: data.ucastvh[index].typfyzucasti,
                                                          zpusob: data.ucastvh[index].zpusob,
                                                          druh:
                                                              (data.ucastvh[index] &&
                                                                  data.ucastvh[index].evot?.length &&
                                                                  data.ucastvh[index].evot![0].druh) ||
                                                              undefined,
                                                          channel:
                                                              (data.ucastvh[index] &&
                                                                  data.ucastvh[index].evot?.length &&
                                                                  data.ucastvh[index].evot![0].channel) ||
                                                              undefined,
                                                          bic:
                                                              (data.ucastvh[index] &&
                                                                  data.ucastvh[index].evot?.length &&
                                                                  data.ucastvh[index].evot![0].bic) ||
                                                              undefined,
                                                          datum: dayjs(data.ucastvh[index].datumoznameni).tz(tz)
                                                      });
                                                  }}
                                              >
                                                  <TranslateText>ZOBRAZIT</TranslateText>
                                              </AnchorComponent>
                                          )
                                      }
                            ]}
                        />
                        {page !== 5 && (
                            <Row>
                                <Col span={24}>
                                    <ButtonComponent
                                        id="pridatDbtn"
                                        buttontext="SRD_PRUVODCE_VH_D_PRIDAT"
                                        onClick={() => {
                                            setOpenUcast(true);
                                            formModal.setFieldsValue({
                                                zpusob: undefined,
                                                datum: undefined
                                            });
                                        }}
                                    />
                                </Col>
                            </Row>
                        )}
                        <Modal
                            destroyOnClose={true}
                            title={<TranslateText>SRD_CIS_UDALOST_VH</TranslateText>}
                            open={openUcast}
                            onCancel={() => {
                                setOpenUcast(false);
                                setDataUcast({});
                                setBodEditIndex(-1);
                            }}
                            afterClose={() => {
                                setOpenUcast(false);
                                setDataUcast({});
                                setBodEditIndex(-1);
                            }}
                            footer={null}
                        >
                            {openUcast && (
                                <FormWrapper
                                    preserve={false}
                                    outForm={formModal}
                                    onFinish={() => {
                                        if (!dataUcast) return;
                                        setData(val => {
                                            const dtucast: IPruvodceVHUcastvh = JSON.parse(JSON.stringify(dataUcast));
                                            dataUcast.zpusob === EZpusob.PHYS &&
                                                data.datumkonani &&
                                                (dtucast.datumoznameni = data.datumkonani);
                                            const prevS = val;
                                            if (bodEditIndex > -1) {
                                                prevS.ucastvh[bodEditIndex] = dtucast;
                                                return prevS;
                                            }
                                            dataUcast.zpusob === EZpusob.EVOT
                                                ? (dtucast.evot = [
                                                      {
                                                          url: dataUcast.evot![0].url,
                                                          druh: dataUcast.evot![0].druh,
                                                          ...(dataUcast.evot![0].bic && {
                                                              bic: dataUcast.evot![0].bic
                                                          }),
                                                          ...(dataUcast.evot![0].channel && {
                                                              channel: dataUcast.evot![0].channel
                                                          })
                                                      }
                                                  ])
                                                : null;
                                            const state = {
                                                ...prevS,
                                                ucastvh: !prevS.ucastvh.length
                                                    ? [dtucast]
                                                    : concat(prevS.ucastvh, dtucast)
                                            };

                                            return state;
                                        });
                                        setBodEditIndex(-1);
                                        setDataUcast({});
                                        setOpenUcast(false);
                                        setPutData(true);
                                    }}
                                >
                                    <SelectComponent
                                        name="zpusob"
                                        isFormItem={true}
                                        disabled={page === 5}
                                        labelParagraph="SRD_PRUVODCE_VH_D_PRIDAT_ZPUSOB"
                                        defaultValue={dataUcast?.zpusob}
                                        data={zpusobSelectData}
                                        onChange={value => {
                                            value === EZpusob.PHYS
                                                ? formModal.setFieldsValue({
                                                      datum: dayjs(data.datumkonani).tz(tz)
                                                  })
                                                : formModal.setFieldsValue({
                                                      datum: undefined
                                                  });
                                            setDataUcast(val => ({
                                                ...val,
                                                zpusob: value
                                            }));
                                        }}
                                    />
                                    <DatePickerComponent
                                        name="datum"
                                        label="DATUM_CET"
                                        disabled={dataUcast?.zpusob === EZpusob.PHYS || page === 5}
                                        format={value => `${value.format('DD.MM.YYYY HH:mm')} (CET/CEST)`}
                                        initialValue={
                                            data.datumkonani && dataUcast?.zpusob === EZpusob.PHYS
                                                ? dayjs(data.datumkonani).tz(tz)
                                                : dataUcast?.datumoznameni
                                                  ? dayjs(dataUcast?.datumoznameni).tz(tz)
                                                  : undefined
                                        }
                                        showTime={true}
                                        disabledDate={current =>
                                            current.tz(tz).format('YYYY-MM-DD') < dayjs().tz(tz).format('YYYY-MM-DD')
                                        }
                                        onChange={value =>
                                            setDataUcast(val => ({
                                                ...val,
                                                datumoznameni: value.utc().tz(tz).format()
                                            }))
                                        }
                                    />
                                    {dataUcast?.zpusob === EZpusob.MAIL && (
                                        <>
                                            <InputComponent
                                                name="ulice"
                                                label="ULICE_CP"
                                                disabled={page === 5}
                                                initialValue={dataUcast.mail?.ulice}
                                                maxLength={70}
                                                onChange={value =>
                                                    setDataUcast(val => ({
                                                        ...val,
                                                        mail: {
                                                            ...val?.mail,
                                                            ulice: value.target.value
                                                        }
                                                    }))
                                                }
                                            />
                                            <InputComponent
                                                name="mesto"
                                                label="MESTO"
                                                disabled={page === 5}
                                                maxLength={35}
                                                initialValue={dataUcast?.mail?.mesto}
                                                onChange={value =>
                                                    setDataUcast(val => ({
                                                        ...val,
                                                        mail: {
                                                            ...val?.mail,
                                                            mesto: value.target.value
                                                        }
                                                    }))
                                                }
                                            />
                                            <InputComponent
                                                name="psc"
                                                label="PSC"
                                                maxLength={16}
                                                disabled={page === 5}
                                                initialValue={dataUcast?.mail?.psc}
                                                onChange={value =>
                                                    setDataUcast(val => ({
                                                        ...val,
                                                        mail: {
                                                            ...val?.mail,
                                                            psc: value.target.value
                                                        }
                                                    }))
                                                }
                                            />
                                            <SelectComponent
                                                name="stat"
                                                disabled={page === 5}
                                                labelParagraph="STAT"
                                                isFormItem={true}
                                                data={cisStaty}
                                                defaultValue={dataUcast?.mail?.stat}
                                                onChange={value =>
                                                    setDataUcast(val => ({
                                                        ...val,
                                                        mail: {
                                                            ...val?.mail,
                                                            stat: value
                                                        }
                                                    }))
                                                }
                                            />
                                        </>
                                    )}
                                    {dataUcast?.zpusob === EZpusob.EVOT && (
                                        <>
                                            <InputComponent
                                                name="URL"
                                                label="URL"
                                                initialValue={
                                                    dataUcast.evot && dataUcast.evot.length
                                                        ? dataUcast.evot[0].url
                                                        : undefined
                                                }
                                                onChange={value =>
                                                    setDataUcast(val => ({
                                                        ...val,
                                                        evot: [
                                                            {
                                                                url: value.target.value,
                                                                bic:
                                                                    val?.evot && val.evot.length
                                                                        ? val?.evot[0].bic
                                                                        : undefined
                                                            }
                                                        ]
                                                    }))
                                                }
                                                rules={urlValidation()}
                                            />
                                            <SelectComponent
                                                name="druh"
                                                labelParagraph="VoteThroughNetwork"
                                                isFormItem={true}
                                                disabled={page === 5}
                                                data={[
                                                    {
                                                        labelcz: 'VoteDirectlyToIssuer',
                                                        value: 'BIC'
                                                    },
                                                    {
                                                        labelcz: 'VoteChannel',
                                                        value: 'CHANNEL'
                                                    }
                                                ]}
                                                defaultValue={dataUcast?.typfyzucasti}
                                                onChange={value =>
                                                    setDataUcast(val => ({
                                                        ...val,
                                                        evot: [
                                                            {
                                                                druh: value,
                                                                url:
                                                                    val?.evot && val.evot.length
                                                                        ? val?.evot[0].url
                                                                        : undefined
                                                            }
                                                        ]
                                                    }))
                                                }
                                            />

                                            {formModal.getFieldValue('druh') === 'CHANNEL' && (
                                                <SelectComponent
                                                    name="channel"
                                                    labelParagraph="VoteChannel"
                                                    isFormItem={true}
                                                    disabled={page === 5}
                                                    data={[
                                                        {
                                                            labelcz: 'VoteAsPerIntermediaryInformation',
                                                            value: 'VOCI'
                                                        },
                                                        {
                                                            labelcz: 'VoteThroughChain',
                                                            value: 'VOPI'
                                                        }
                                                    ]}
                                                    defaultValue={dataUcast?.typfyzucasti}
                                                    onChange={value =>
                                                        setDataUcast(val => ({
                                                            ...val,
                                                            evot: [
                                                                {
                                                                    ...(val?.evot?.length && val?.evot[0]),
                                                                    channel: value
                                                                }
                                                            ]
                                                        }))
                                                    }
                                                />
                                            )}
                                            {formModal.getFieldValue('druh') === 'BIC' && (
                                                <InputComponent
                                                    name="BIC"
                                                    label="BIC"
                                                    disabled={page === 5}
                                                    initialValue={
                                                        dataUcast.evot && dataUcast.evot.length
                                                            ? dataUcast.evot[0].bic
                                                            : undefined
                                                    }
                                                    onChange={value =>
                                                        setDataUcast(val => ({
                                                            ...val,
                                                            evot: [
                                                                {
                                                                    ...(val?.evot?.length && val?.evot[0]),
                                                                    bic: value.target.value
                                                                }
                                                            ]
                                                        }))
                                                    }
                                                    rules={bicValidation()}
                                                />
                                            )}
                                        </>
                                    )}
                                    {dataUcast?.zpusob === EZpusob.PHYS && (
                                        <SelectComponent
                                            name="typfyzucasti"
                                            labelParagraph="SRD_PRUVODCE_VH_D_PRIDAT_TYPFYZUCASTI"
                                            isFormItem={true}
                                            disabled={page === 5}
                                            data={AdmissionConditionsSelect}
                                            defaultValue={dataUcast?.typfyzucasti}
                                            onChange={value =>
                                                setDataUcast(val => ({
                                                    ...val,
                                                    typfyzucasti: value
                                                }))
                                            }
                                        />
                                    )}
                                    {dataUcast?.zpusob === EZpusob.PRXY && (
                                        <SelectComponent
                                            name="proxytype"
                                            labelParagraph="SRD_PRUVODCE_VH_D_PRIDAT_PROXYTYPE"
                                            isFormItem={true}
                                            disabled={page === 5}
                                            data={proxyTypeSelect}
                                            defaultValue={dataUcast?.proxytype}
                                            onChange={value =>
                                                setDataUcast(val => ({
                                                    ...val,
                                                    proxytype: value
                                                }))
                                            }
                                        />
                                    )}
                                    {dataUcast?.zpusob === EZpusob.MAIL && (
                                        <InputComponent
                                            label="E-mail"
                                            name="email"
                                            notRequired={true}
                                            rules={emailValidation()}
                                            initialValue={dataUcast?.mail?.email}
                                            onChange={value =>
                                                setDataUcast(val => ({
                                                    ...val,
                                                    mail: {
                                                        ...val?.mail,
                                                        email: value.target.value
                                                    }
                                                }))
                                            }
                                        />
                                    )}
                                    <Row className="button-row">
                                        <Col>
                                            <ButtonComponent
                                                id="zavritModalDbtn"
                                                type="default"
                                                buttontext="ZAVRIT"
                                                onClick={() => {
                                                    setDataUcast({});
                                                    setOpenUcast(false);
                                                }}
                                            />
                                        </Col>
                                        {page !== 5 && (
                                            <Col>
                                                <ButtonComponent
                                                    id="submitModalDbtn"
                                                    buttontext={bodEditIndex > -1 ? 'BUTTON_UPRAVIT' : 'PRIDAT'}
                                                    onClick={() => formModal.submit()}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                </FormWrapper>
                            )}
                        </Modal>
                    </>
                )}
                {[4, 5].includes(page) && (
                    <>
                        <Title level={5} dividerTitle={true} text="SRD_PRUVODCE_VH_E" />
                        <TableComponent
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                            rowKey={(_, index) => index || 0}
                            style={{
                                margin: '30px 0px'
                            }}
                            dataSource={openBod ? [...(data?.bodyvh || [])] : data?.bodyvh}
                            pagination={false}
                            columns={[
                                {
                                    dataIndex: 'jmeno',
                                    key: 'jmeno',
                                    render: (_text: IPruvodceVHBodyvh, row: IPruvodceVHBodyvh) => (
                                        <p
                                            style={{
                                                margin: 0
                                            }}
                                        >
                                            {row.isssrlabl}
                                        </p>
                                    )
                                },
                                page !== 5
                                    ? {
                                          width: 100,
                                          render: (
                                              _text: IPruvodceVHBodyvh,
                                              _row: IPruvodceVHBodyvh,
                                              index: number
                                          ) => (
                                              <>
                                                  <AnchorComponent
                                                      id={`upravitEbtn${index}`}
                                                      key={index}
                                                      onClick={() => {
                                                          setOpenBod(true);
                                                          setDataBod(data.bodyvh[index]);
                                                          formModal.setFieldsValue({
                                                              ...data.bodyvh[index],
                                                              hlasovani: data.bodyvh[index].hlasovani?.map(
                                                                  e => e.hlasovani
                                                              ),
                                                              kvorumtype: data.bodyvh[index].kvorum?.pocet
                                                                  ? EKvorumEnums.POCET
                                                                  : data.bodyvh[index].kvorum?.procenta
                                                                    ? EKvorumEnums.PROCENTA
                                                                    : EKvorumEnums.NE,
                                                              kvorumamount:
                                                                  data.bodyvh[index].kvorum?.pocet ||
                                                                  data.bodyvh[index].kvorum?.procenta ||
                                                                  undefined,
                                                              forinfonly:
                                                                  typeof data.bodyvh[index].forinfonly === 'boolean'
                                                                      ? data.bodyvh[index].forinfonly
                                                                          ? EAnoNe.ANO
                                                                          : EAnoNe.NE
                                                                      : undefined
                                                          });
                                                          setBodEditIndex(index);
                                                      }}
                                                  >
                                                      <TranslateText>BUTTON_UPRAVIT</TranslateText>
                                                  </AnchorComponent>
                                                  <AnchorComponent
                                                      id={`odebratEbtn${index}`}
                                                      style={{
                                                          marginLeft: 16
                                                      }}
                                                      key={index + 'b'}
                                                      onClick={() => {
                                                          setData(val => {
                                                              const newState = [...val.bodyvh];
                                                              newState.splice(index, 1);
                                                              return {
                                                                  ...val,
                                                                  bodyvh: newState
                                                              };
                                                          });
                                                          setPutData(true);
                                                      }}
                                                  >
                                                      <TranslateText>ODEBRAT</TranslateText>
                                                  </AnchorComponent>
                                              </>
                                          )
                                      }
                                    : {
                                          width: 100,
                                          render: (
                                              _text: IPruvodceVHBodyvh,
                                              _row: IPruvodceVHBodyvh,
                                              index: number
                                          ) => (
                                              <>
                                                  <AnchorComponent
                                                      id={`zobrazitEbtn${index}`}
                                                      key={index}
                                                      onClick={() => {
                                                          setOpenBod(true);
                                                          setDataBod(data.bodyvh[index]);
                                                          formModal.setFieldsValue({
                                                              ...data.bodyvh[index],
                                                              hlasovani: data.bodyvh[index].hlasovani?.map(
                                                                  e => e.hlasovani
                                                              ),
                                                              kvorumtype: data.bodyvh[index].kvorum?.pocet
                                                                  ? EKvorumEnums.POCET
                                                                  : data.bodyvh[index].kvorum?.procenta
                                                                    ? EKvorumEnums.PROCENTA
                                                                    : EKvorumEnums.NE,
                                                              kvorumamount:
                                                                  data.bodyvh[index].kvorum?.pocet ||
                                                                  data.bodyvh[index].kvorum?.procenta ||
                                                                  undefined,
                                                              forinfonly:
                                                                  typeof data.bodyvh[index].forinfonly === 'boolean'
                                                                      ? data.bodyvh[index].forinfonly
                                                                          ? EAnoNe.ANO
                                                                          : EAnoNe.NE
                                                                      : undefined
                                                          });
                                                          setBodEditIndex(index);
                                                      }}
                                                  >
                                                      <TranslateText>ZOBRAZIT</TranslateText>
                                                  </AnchorComponent>
                                              </>
                                          )
                                      }
                            ]}
                        />
                        {page !== 5 && (
                            <Row>
                                <Col span={24}>
                                    <ButtonComponent
                                        id="pridatEbtn"
                                        buttontext="SRD_PRUVODCE_VH_E_PRIDAT"
                                        onClick={() => {
                                            setOpenBod(true);
                                            formModal.setFieldsValue({
                                                isssrlabl: undefined,
                                                titl: undefined,
                                                titlen: undefined,
                                                desc: undefined,
                                                descen: undefined,
                                                votetp: undefined,
                                                forinfonly: undefined,
                                                urladdress: undefined,
                                                hlasovani: undefined,
                                                kvorum: {
                                                    pocet: undefined,
                                                    procenta: undefined
                                                }
                                            });
                                        }}
                                        disabled={page === 5}
                                    />
                                </Col>
                            </Row>
                        )}
                        <Modal
                            destroyOnClose={true}
                            title={<TranslateText>SRD_CIS_UDALOST_VH</TranslateText>}
                            open={openBod}
                            onCancel={() => {
                                setOpenBod(false);
                                setDataBod({});
                                setBodEditIndex(-1);
                            }}
                            afterClose={() => {
                                setOpenBod(false);
                                setDataBod({});
                                setBodEditIndex(-1);
                            }}
                            footer={null}
                        >
                            {openBod && (
                                <FormWrapper
                                    preserve={false}
                                    outForm={formModal}
                                    onFinish={() => {
                                        if (!dataBod) return;
                                        /* kontrola specialnych pismen kvoli backendu (jedno pismeno pocita ako 2 a robi to maxlength problem) */
                                        const titlcz = dataBod.titl || '';
                                        const titlen = dataBod.titlen || '';
                                        const descz = dataBod.desc || '';
                                        const descen = dataBod.descen || '';

                                        const titlCzValidated =
                                            (titlcz.match(/[á-žÁ-Ž]/g) || []).length + titlcz.length <= 350;
                                        const titlEnValidated =
                                            (titlen.match(/[á-žÁ-Ž]/g) || []).length + titlen.length <= 350;
                                        const descCzValidated =
                                            (descz.match(/[á-žÁ-Ž]/g) || []).length + descz.length <= 1025;
                                        const descEnValidated =
                                            (descen.match(/[á-žÁ-Ž]/g) || []).length + descen.length <= 1025;

                                        if (titlCzValidated && titlEnValidated && descCzValidated && descEnValidated) {
                                            setData(val => {
                                                const dtucast: IPruvodceVHBodyvh = {
                                                    ...(dataBod.isssrlabl && {
                                                        isssrlabl: dataBod.isssrlabl
                                                    }),
                                                    ...(dataBod.titl && {
                                                        titl: dataBod.titl
                                                    }),
                                                    ...(dataBod.titlen && {
                                                        titlen: dataBod.titlen
                                                    }),
                                                    ...(dataBod.desc && {
                                                        desc: dataBod.desc
                                                    }),
                                                    ...(dataBod.descen && {
                                                        descen: dataBod.descen
                                                    }),
                                                    forinfonly: dataBod.forinfonly,
                                                    ...(dataBod.votetp && {
                                                        votetp: dataBod.votetp
                                                    }),
                                                    ...(dataBod.urladdress && {
                                                        urladdress: dataBod.urladdress
                                                    }),
                                                    ...(dataBod.hlasovani && {
                                                        hlasovani: dataBod.hlasovani
                                                    }),
                                                    ...((dataBod.kvorumamount || dataBod.kvorum || dataBod.typprahu) &&
                                                        (dataBod.kvorumamount
                                                            ? {
                                                                  kvorum: {
                                                                      ...(dataBod.kvorumtype === EKvorumEnums.POCET
                                                                          ? {
                                                                                pocet: dataBod.kvorumamount,
                                                                                ...((dataBod.typprahu ||
                                                                                    dataBod.kvorum?.typprahu) && {
                                                                                    typprahu:
                                                                                        dataBod.typprahu ||
                                                                                        dataBod.kvorum?.typprahu
                                                                                })
                                                                            }
                                                                          : {
                                                                                procenta: dataBod.kvorumamount,
                                                                                ...((dataBod.typprahu ||
                                                                                    dataBod.kvorum?.typprahu) && {
                                                                                    typprahu:
                                                                                        dataBod.typprahu ||
                                                                                        dataBod.kvorum?.typprahu
                                                                                })
                                                                            })
                                                                  }
                                                              }
                                                            : {
                                                                  kvorum: {
                                                                      ...dataBod.kvorum,
                                                                      ...((dataBod.typprahu ||
                                                                          dataBod.kvorum?.typprahu) && {
                                                                          typprahu:
                                                                              dataBod.typprahu ||
                                                                              dataBod.kvorum?.typprahu
                                                                      })
                                                                  }
                                                              }))
                                                };
                                                const prevS = val;
                                                if (bodEditIndex > -1) {
                                                    prevS.bodyvh[bodEditIndex] = dtucast;
                                                    return prevS;
                                                }
                                                const state = {
                                                    ...prevS,
                                                    bodyvh: !prevS.bodyvh.length
                                                        ? [dtucast]
                                                        : concat(prevS.bodyvh, dtucast)
                                                };
                                                return state;
                                            });
                                            setDataBod({});
                                            setOpenBod(false);
                                            setBodEditIndex(-1);
                                            setPutData(true);
                                        } else {
                                            setPopisMaxLengthErr({
                                                nazev: !titlCzValidated,
                                                popis: !descCzValidated,
                                                nazevEN: !titlEnValidated,
                                                popisEN: !descEnValidated
                                            });
                                        }
                                    }}
                                >
                                    <InputComponent
                                        name="isssrlabl"
                                        label="SRD_PRUVODCE_VH_E_PRIDAT_ISSSRLABL"
                                        maxLength={35}
                                        initialValue={dataBod?.isssrlabl}
                                        disabled={page === 5}
                                        onChange={value =>
                                            setDataBod(val => ({
                                                ...val,
                                                isssrlabl: value.target.value
                                            }))
                                        }
                                    />
                                    <InputComponent
                                        name="titl"
                                        label="SRD_PRUVODCE_VH_E_PRIDAT_TITL"
                                        initialValue={dataBod?.titl}
                                        disabled={page === 5}
                                        onChange={value => {
                                            popisMaxLengthErr.nazev &&
                                                setPopisMaxLengthErr(pv => {
                                                    return {
                                                        ...pv,
                                                        nazev: false
                                                    };
                                                });
                                            setDataBod(val => ({
                                                ...val,
                                                titl: value.target.value
                                            }));
                                        }}
                                        extramessage={popisMaxLengthErr.nazev ? 'NEVALIDNI_POCET_ZNAKU' : ''}
                                    />

                                    <InputComponent
                                        name="titlen"
                                        label="SRD_PRUVODCE_VH_E_PRIDAT_TITL_EN"
                                        initialValue={dataBod?.titlen}
                                        disabled={page === 5}
                                        onChange={value => {
                                            popisMaxLengthErr.nazevEN &&
                                                setPopisMaxLengthErr(pv => {
                                                    return {
                                                        ...pv,
                                                        nazevEN: false
                                                    };
                                                });
                                            setDataBod(val => ({
                                                ...val,
                                                titlen: value.target.value
                                            }));
                                        }}
                                        extramessage={popisMaxLengthErr.nazevEN ? 'NEVALIDNI_POCET_ZNAKU' : ''}
                                    />
                                    <InputComponent
                                        name="desc"
                                        label="POPIS"
                                        initialValue={dataBod?.desc}
                                        disabled={page === 5}
                                        onChange={value => {
                                            popisMaxLengthErr.popis &&
                                                setPopisMaxLengthErr(pv => {
                                                    return {
                                                        ...pv,
                                                        popis: false
                                                    };
                                                });
                                            setDataBod(val => ({
                                                ...val,
                                                desc: value.target.value
                                            }));
                                        }}
                                        extramessage={popisMaxLengthErr.popis ? 'NEVALIDNI_POCET_ZNAKU' : ''}
                                    />
                                    <InputComponent
                                        name="descen"
                                        label="POPIS_EN"
                                        initialValue={dataBod?.descen}
                                        disabled={page === 5}
                                        onChange={value => {
                                            popisMaxLengthErr.popisEN &&
                                                setPopisMaxLengthErr(pv => {
                                                    return {
                                                        ...pv,
                                                        popisEN: false
                                                    };
                                                });
                                            setDataBod(val => ({
                                                ...val,
                                                descen: value.target.value
                                            }));
                                        }}
                                        extramessage={popisMaxLengthErr.popisEN ? 'NEVALIDNI_POCET_ZNAKU' : ''}
                                    />
                                    <InputComponent
                                        name="urladdress"
                                        label="SRD_PRUVODCE_VH_C_URLSCHUZKY"
                                        initialValue={dataBod?.urladdress}
                                        disabled={page === 5}
                                        onChange={value =>
                                            setDataBod(val => ({
                                                ...val,
                                                urladdress: value.target.value
                                            }))
                                        }
                                        notRequired={true}
                                        rules={urlValidation()}
                                    />

                                    <SelectComponent
                                        name="forinfonly"
                                        labelParagraph="SRD_PRUVODCE_VH_E_PRIDAT_FORINFOONLY"
                                        isFormItem={true}
                                        disabled={page === 5}
                                        defaultValue={
                                            typeof dataBod?.forinfonly === 'boolean'
                                                ? dataBod?.forinfonly
                                                    ? EAnoNe.ANO
                                                    : EAnoNe.NE
                                                : undefined
                                        }
                                        data={identifikaceOsobySelect}
                                        onChange={value =>
                                            setDataBod(val => ({
                                                ...val,
                                                forinfonly: value === EAnoNe.ANO ? true : false
                                            }))
                                        }
                                    />
                                    {typeof dataBod?.forinfonly === 'boolean' && !dataBod.forinfonly && (
                                        <SelectComponent
                                            name="kvorumtype"
                                            labelParagraph="SRD_PRUVODCE_VH_E_KVORUM_SELECT"
                                            isFormItem={true}
                                            disabled={page === 5}
                                            defaultValue={
                                                dataBod?.kvorum?.pocet
                                                    ? EKvorumEnums.POCET
                                                    : dataBod?.kvorum?.procenta
                                                      ? EKvorumEnums.PROCENTA
                                                      : undefined
                                            }
                                            data={kvorumSelect}
                                            onChange={value => {
                                                setDataBod(val => ({
                                                    ...val,
                                                    kvorumtype: value !== EKvorumEnums.NE ? value : undefined,
                                                    kvorumamount: undefined,
                                                    kvorum: undefined
                                                }));
                                                formModal.setFieldsValue({
                                                    kvorumamount: undefined
                                                });
                                            }}
                                        />
                                    )}
                                    {(dataBod?.kvorum?.pocet || dataBod?.kvorum?.procenta || dataBod?.kvorumtype) &&
                                        typeof dataBod?.forinfonly === 'boolean' &&
                                        !dataBod.forinfonly && (
                                            <InputNumberComponent
                                                name="kvorumamount"
                                                label={
                                                    dataBod?.kvorumtype === EKvorumEnums.PROCENTA ||
                                                    dataBod?.kvorum?.procenta
                                                        ? 'SRD_PRUVODCE_VH_E_KVORUM_SELECT_PROCENTA'
                                                        : 'SRD_PRUVODCE_VH_E_KVORUM_SELECT_POCET'
                                                }
                                                rules={
                                                    dataBod?.kvorumtype === EKvorumEnums.POCET || dataBod?.kvorum?.pocet
                                                        ? numberValidation()
                                                        : procentaValidation()
                                                }
                                                disabled={page === 5}
                                                initialValue={
                                                    dataBod?.kvorum?.pocet || dataBod?.kvorum?.procenta || undefined
                                                }
                                                maxLength={5}
                                                onChangeCustom={val => {
                                                    setDataBod(prev => ({
                                                        ...prev,
                                                        kvorumamount: String(val)
                                                    }));
                                                }}
                                            />
                                        )}
                                    {(dataBod?.kvorum?.pocet || dataBod?.kvorum?.procenta || dataBod?.kvorumtype) &&
                                        typeof dataBod?.forinfonly === 'boolean' &&
                                        !dataBod.forinfonly && (
                                            <SelectComponent
                                                name="typprahu"
                                                labelParagraph="SRD_PRUVODCE_VH_E_PRIDAT_PRAH"
                                                isFormItem={true}
                                                disabled={page === 5}
                                                defaultValue={dataBod.typprahu || dataBod?.kvorum?.typprahu}
                                                data={prahSelect}
                                                onChange={value =>
                                                    setDataBod(val => ({
                                                        ...val,
                                                        typprahu: value
                                                    }))
                                                }
                                            />
                                        )}

                                    {dataBod?.forinfonly === false && (
                                        <>
                                            <SelectComponent
                                                name="votetp"
                                                labelParagraph="SRD_PRUVODCE_VH_E_PRIDAT_VOTETP"
                                                isFormItem={true}
                                                data={votetpData}
                                                disabled={page === 5}
                                                defaultValue={dataBod.votetp}
                                                onChange={value =>
                                                    setDataBod(val => ({
                                                        ...val,
                                                        votetp: value
                                                    }))
                                                }
                                            />
                                            <SelectComponent
                                                name="hlasovani"
                                                labelParagraph="SRD_PRUVODCE_VH_E_PRIDAT_HLASOVANI"
                                                isFormItem={true}
                                                data={hlasovaniData}
                                                disabled={page === 5}
                                                mode="multiple"
                                                defaultValue={dataBod.hlasovani?.map(e => e.hlasovani)}
                                                onChange={value =>
                                                    setDataBod(val => ({
                                                        ...val,
                                                        hlasovani: value.map((e: string) => {
                                                            return {
                                                                hlasovani: e
                                                            };
                                                        })
                                                    }))
                                                }
                                            />
                                        </>
                                    )}

                                    <Row className="button-row">
                                        <Col>
                                            <ButtonComponent
                                                id="zavritModalEbtn"
                                                buttontext="ZAVRIT"
                                                onClick={() => {
                                                    setDataBod({});
                                                    setOpenBod(false);
                                                }}
                                                type="default"
                                            />
                                        </Col>
                                        {page !== 5 && (
                                            <Col>
                                                <ButtonComponent
                                                    id="submitModalEbtn"
                                                    buttontext={bodEditIndex > -1 ? 'BUTTON_UPRAVIT' : 'PRIDAT'}
                                                    onClick={() => formModal.submit()}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                </FormWrapper>
                            )}
                        </Modal>
                    </>
                )}
                {page === 5 && (
                    <DatePickerComponent
                        name="datumzverejneni"
                        label="DATUM_UVEREJNENI"
                        format={value => `${value.utc().tz(tz).format('DD.MM.YYYY HH:mm')} (CET/CEST)`}
                        initialValue={undefined}
                        onChange={(val: dayjs.Dayjs | null) => {
                            setData(pv => ({
                                ...pv,
                                ...(val && {
                                    datumzverejneni: val.utc().tz(tz).format()
                                })
                            }));
                        }}
                        showTime={true}
                        disabledDate={current =>
                            current.tz(tz).format('YYYY-MM-DD') < dayjs().tz(tz).format('YYYY-MM-DD')
                        }
                    />
                )}
                {page === 5 && (
                    <Row
                        className="button-row"
                        style={{
                            marginBottom: 10
                        }}
                    >
                        <Col>
                            <ButtonComponent
                                id="stahnoutXmlbtn"
                                icon={<DownloadOutlined />}
                                buttontext="SRD_STAHNOUT_XML"
                                loading={downloading === 'xml'}
                                type="default"
                                onClick={() => form.validateFields().then(() => downloadZhrnuti('xml'))}
                            />
                        </Col>
                        <Col>
                            <ButtonComponent
                                id="stahnoutPdfbtn"
                                icon={<DownloadOutlined />}
                                buttontext="STAHNOUT_PDF"
                                loading={downloading === 'pdf'}
                                type="default"
                                onClick={() => form.validateFields().then(() => downloadZhrnuti('pdf'))}
                            />
                        </Col>
                    </Row>
                )}
                <Row className="button-row">
                    {page !== 1 && (
                        <Col>
                            <ButtonComponent
                                buttontext="ZPET"
                                onClick={() => setPage(pv => pv - 1)}
                                type="default"
                                id="zpetPruvodceBtn"
                            />
                        </Col>
                    )}
                    {page !== 5 && (
                        <Col>
                            <ButtonComponent
                                id="pokracovatBtn"
                                buttontext="POKRACOVAT"
                                onClick={() => {
                                    if (page > 2) nextPage();
                                    else {
                                        form.validateFields().then(() => {
                                            nextPage();
                                        });
                                    }
                                }}
                                htmlType="button"
                                disabled={page === 3 && !data.ucastvh.length}
                            />
                        </Col>
                    )}
                    {page === 5 && (
                        <Col>
                            <ButtonComponent id="odeslatBtn" buttontext="ODESLAT" onClick={() => form.submit()} />
                        </Col>
                    )}
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};
